/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { CommunicationType } from '@gh/builder-shared-data';
import {
  ContextRoute,
  createDefaultGridModel,
  getContextRouteInitData,
  htmlTextCellRenderer,
  notEmptyCellRenderer,
  rendererIconByName, ROW_UNREAD_CLASS, ROW_WARNING_CLASS,
} from '@gh/common-internal-shared';
import { DateTimeTypes, EnumDictionary } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { buildDateTimeColumn, GridModel, ManualGridDefinitionWithModel } from '@gh/core-ui-grid';
import { ColDef, GridOptions } from 'ag-grid-community';
import { assign } from 'lodash';
import { getLeadCommunicationId } from '../../../resources/lead.model';
import { CommunicationListItem } from '@gh/builder-shared';
import { currentDate } from '@gh/core-util';

@Injectable()
export class LeadDetailsDcFormGridDefinition extends ManualGridDefinitionWithModel {
  preselected: ID[];
  multiselection?: boolean;
  communicationTypes: EnumDictionary<CommunicationType>;

  constructor(private translationService: TranslationService,
              private route: ContextRoute) {
    super();
    const { communicationTypes } = getContextRouteInitData(route);
    assign(this, { communicationTypes });
  }

  buildColumnDefs(): ColDef[] {
    return [{
      field: 'communicationType',
      headerName: this.translationService.get('lbl_type'),
      width: 250,
      valueFormatter: (params: any) => params.node.data.communicationType
        ? this.communicationTypes.get(params.node.data.communicationType)
        : '',
    }, {
      field: 'created',
      headerName: this.translationService.get('lbl_created'),
      ...buildDateTimeColumn(DateTimeTypes.DateTime),
    }, {
      field: 'createdByName',
      headerName: this.translationService.get('lbl_sender'),
      valueFormatter: (params: any) => {
        if (params.node.data.createdByName) {
          return params.node.data.createdByName;
        }
        if (params.node.data.milestoneEvent &&
          (params.node.data.communicationType === CommunicationType.Email
            || params.node.data.communicationType === CommunicationType.Email)) {
          return this.translationService.get('lbl_milestone_event');
        }
        return '';
      },
    }, {
      field: 'subject',
      headerName: this.translationService.get('lbl_subject'),
    }, {
        field: 'location',
        headerName: this.translationService.get('lbl_location'),
    }, {
      field: 'message',
      headerName: this.translationService.get('lbl_message'),
      cellRenderer: htmlTextCellRenderer,
    }, {
      field: 'attachments',
      headerName: this.translationService.get('lbl_attachments'),
      cellRenderer: notEmptyCellRenderer(rendererIconByName('attach-file')),
    }, {
      width: 300,
      field: 'milestoneEvent',
      headerName: this.translationService.get('lbl_milestone_event'),
    }];
  }

  buildGridOptions(): GridOptions {
    const date = currentDate();
    return {
      rowClassRules: {
        [ROW_UNREAD_CLASS]: ({data}: { data: CommunicationListItem }) =>
          data.communicationType === CommunicationType.IncomingSms && !data.isMarkedAsRead,
        [ROW_WARNING_CLASS]: ({data}: { data: CommunicationListItem }) => !data.isDone &&
          (data.communicationType === CommunicationType.SalesActivity
            || data.communicationType === CommunicationType.JobActivity
            || data.communicationType === CommunicationType.ContactActivity)
          && !!data.startDate && data.startDate < date,
      },
    };
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel({
      getId: getLeadCommunicationId,
    });
  }
}
